import {
  DataGridPro, GridColDef, useGridApiRef, GridValidRowModel, GridApiPro,
} from '@mui/x-data-grid-pro';
import {
  MutableRefObject,
  useCallback,
  useMemo, useRef, useState,
} from 'react';
import { useTheme } from '@mui/material';
import Card from '@mui/material/Card';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import startCase from 'lodash/startCase';
import get from 'lodash/get';
import { TCustomTheme } from '@mgm/common/theme';
import { useCustomTheme } from '@mgm/common/hooks/useCustomTheme';
import { useScreenResize } from '@mgm/common/hooks/useResize';
import { BaseLabel, MemoizedBaseAvatar } from '@mgm/common/components/base';
import { dateFormatTZ } from '@mgm/common/utils';
import ExcelJS, { Font } from 'exceljs';
import { saveAs } from 'file-saver';
import { CustomNoRowsOverlay, Iconify } from '@mgm/common/components';
import { PrintWrapper } from '../../../../layout/wrapper/PrintWrapper';
import { useAppDispatch, useAppSelector } from '../../../../hooks/useAppStore';
import { getCurrencyByCode, getIsPrintMode } from '../../../../store/common/selectors';
import { useTable } from '../../../../hooks/useTable';
import { MemoizedFilterTabs } from '../../../../components/base';
import {
  getCommissionPlans,
  getCommissionsSummary,
  getCommissionView,
  getTableCommissionView,
} from '../../../../store/commissions/selectors';
import { getCommissionsByDate, mapWorkTimesByDate } from '../../../../utils/helpers/commission.helper';
import { useAttributedOrdersOrdersFilter } from '../../Orders/hooks/useAttributedOrdersOrdersFilter';
import { TBonus } from '../../../../types';
import { commissionsActions } from '../../../../store/commissions/slice';
import { calculateOrderCommissionByPlan } from '../../../../utils/helpers/orders.helper';
import { getIsOpenEmployeesExportMode, getSheets } from '../../../../store/sheets/selectors';
import { useCommissions } from '../../../../hooks/useCommissions';
import { OrdersCountLoader, OrdersTableHeader } from '../../Shared/components';
import { INDEX_TO_COLOR } from '../../../../data';
import { getAuthenticateUser } from '../../../../store/authenticate/selectors';
import { usePermissions } from '../../../../hooks/useCommon';
import { useCommonOrders } from '../../../../hooks/useCommonOrders';
import { sheetsActions } from '../../../../store/sheets/slice';

const CommissionsTable = () => {
  const theme = useTheme() as TCustomTheme;
  const { isMobile, isLight } = useCustomTheme(theme);
  const commissionView = useAppSelector(getCommissionView);
  const { personnel, shop } = useAppSelector(getAuthenticateUser);

  const apiRef = useGridApiRef() as MutableRefObject<GridApiPro>;
  const {
    localText, handleScrollTable, exporting,
  } = useTable(apiRef);
  const { t } = useTranslation(['common', 'inputs', 'commissions', 'timesheets', 'dashboard', 'orders', 'team']);

  const { pageHeight, resizableDiv, isDesktopSmallScreen } = useScreenResize(59);
  const componentRef = useRef<HTMLDivElement | null>(null);
  const isPrintMode = useAppSelector(getIsPrintMode);
  const commissionsSummary = useAppSelector(getCommissionsSummary);

  const getCurrency = useAppSelector(getCurrencyByCode);
  const { permissions } = usePermissions();
  const { getAttributedOrdersByFilters } = useAttributedOrdersOrdersFilter();

  const commissionPlans = useAppSelector(getCommissionPlans);
  const dispatch = useAppDispatch();
  const tableCommissionView = useAppSelector(getTableCommissionView);
  const sheets = useAppSelector(getSheets);
  const { displayCommissionOptions } = useCommissions();
  const isOpenEmployeesExportMode = useAppSelector(getIsOpenEmployeesExportMode);

  const [isFullTable, setIsFullTable] = useState(false);

  const {
    setIsFetchByDate,
  } = useCommonOrders({
    prevTab: '1',
  });

  const filteredCommissionsSummary = useMemo(() => {
    const userSummary = permissions.isAdmin
      ? commissionsSummary
      : commissionsSummary.filter((sm) => sm.user?.locations?.some((userLocation) => personnel.locations?.some(
        (personnelLocation) => personnelLocation.id === userLocation.id,
      )));

    return userSummary.map((summary) => {
      const updatedSummary = { ...summary };

      const { user, totalSales } = updatedSummary;

      const commissionsByDate = getCommissionsByDate(getAttributedOrdersByFilters, user, sheets, shop.shopTimezone);

      updatedSummary.dailyCommissions = commissionsByDate.reduce(
        (sum: number, row) => sum
          + (parseFloat(
            calculateOrderCommissionByPlan(
              row.netSales.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              }),
              user.commissionPlan,
              row.lineItems,
            )?.totalCommission?.toString() || '0',
          )),
        0,
      );

      if (user?.commissionPlan?.bonuses) {
        let aggregatedTotalSales = totalSales;

        if (user?.commissionPlan?.bonusBase === 'Locations') {
          const managerLocations = user.locations?.map((location) => location.id) || [];

          aggregatedTotalSales = commissionsSummary
            .filter((member) => {
              const isEmployee = member.user?.permissionLevel === 'Employee';
              const hasSharedLocation = member.user?.locations?.some((location) => managerLocations.includes(location.id));
              const isNotManager = member.user?.email !== user.email;

              return isEmployee && hasSharedLocation && isNotManager;
            })
            .reduce((acc, employee) => acc + employee.totalSales, 0);
        }

        const bonusesApplied = user.commissionPlan.bonuses
          .filter((bonus) => {
            const threshold = typeof bonus.from === 'string' ? parseFloat(bonus.from) : bonus.from;
            return aggregatedTotalSales >= threshold;
          })
          .map((bonus) => {
            let totalBonus: string | number = 0;
            if (bonus.fixedType === 'flat') {
              totalBonus = bonus.fixedAmount;
            } else if (bonus.fixedType === 'percent') {
              const amount = typeof bonus.fixedAmount === 'string' ? parseFloat(bonus.fixedAmount) : bonus.fixedAmount;
              totalBonus = (aggregatedTotalSales / 100) * amount;
            }

            return {
              ...bonus,
              totalBonus,
            };
          });

        return {
          ...updatedSummary,
          bonusesApplied,
        };
      }

      return updatedSummary;
    }).filter((member) => {
      if (commissionView === 0) {
        return member.ordersCount > 0;
      }
      return true;
    });
  }, [commissionsSummary, commissionView, personnel, permissions, sheets]);

  const handleFilterStatus = (newValue: number) => {
    dispatch(commissionsActions.setCommissionView(Number(newValue)));
  };

  const exportCommissionsData = useCallback(async () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Commissions');

    const border = {
      top: { style: 'thin' },
      bottom: { style: 'thin' },
      left: { style: 'thin' },
      right: { style: 'thin' },
    };

    const fontConfig: Partial<Font> = {
      name: 'Public Sans Variable',
      size: 11,
    };

    const headerStyle = {
      font: { ...fontConfig, bold: true },
      fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFD3D3D3' } },
      alignment: { vertical: 'middle', horizontal: 'center' },
      border,
    };

    const subHeaderStyle = {
      font: { ...fontConfig, bold: true },
      fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFD3D3D3' } },
      alignment: { vertical: 'middle', horizontal: 'center' },
      border: {
        top: { style: 'thin' },
        bottom: { style: 'thin' },
        left: { style: 'thin' },
        right: { style: 'thin' },
      },
    };

    const cellStyle = {
      font: fontConfig,
      alignment: { vertical: 'middle', horizontal: 'center' },
      border: {
        top: { style: 'thin' },
        bottom: { style: 'thin' },
        left: { style: 'thin' },
        right: { style: 'thin' },
      },
    };

    worksheet.columns = [
      { key: 'name', width: 20 },
      { key: 'ordersCount', width: 15 },
      { key: 'productsCount', width: 15 },
      { key: 'totalSales', width: 15 },
      { key: 'commissionPlan', width: 20 },
      { key: 'totalCommissions', width: 18 },
      { key: 'bonuses', width: 15 },
      { key: 'totalCompensation', width: 20 },
    ];

    const selectedRows = apiRef.current.getSelectedRows();
    const filteredMembers = Array.from(selectedRows.values());

    // eslint-disable-next-line no-restricted-syntax
    for (const member of (filteredMembers as any)) {
      const {
        user,
        ordersCount,
        productsCount,
        totalSales,
        totalCommissions,
        bonusesApplied = [],
      } = member;

      const name = startCase(user?.name);
      const email = user?.email;
      const commissionPlan = user?.commissionPlan?.name || commissionPlans[0]?.name;
      const bonuses = bonusesApplied.reduce((acc: number | string, bonus: { totalBonus: any }) => acc + (bonus.totalBonus || 0), 0);
      const totalCompensation = (totalCommissions || 0) + bonuses;

      const headerRow = worksheet.addRow([
        t('name', { ns: 'inputs' }),
        t('ordersCount', { ns: 'commissions' }),
        t('productsCount', { ns: 'commissions' }),
        t('totalSales', { ns: 'commissions' }),
        t('commissionPlan', { ns: 'orders' }),
        t('totalCommissions', { ns: 'commissions' }),
        t('bonuses', { ns: 'commissions' }),
        t('totalCompensation', { ns: 'orders' }),
      ]);

      // eslint-disable-next-line no-return-assign,no-param-reassign
      headerRow.eachCell((cell: any) => cell.style = headerStyle);

      const formattedTotalSales = `${getCurrency().mark}${totalSales?.toFixed(2)}`;
      const formattedTotalCompensation = `${getCurrency().mark}${totalCompensation?.toFixed(2)}`;
      const formattedTotalCommissions = `${getCurrency().mark}${totalCommissions?.toFixed(2)}`;
      const formattedBonuses = `${getCurrency().mark}${bonuses?.toFixed(2)}`;

      const summaryRow = worksheet.addRow([
        name,
        ordersCount || 0,
        productsCount || 0,
        formattedTotalSales,
        commissionPlan,
        Number.isNaN(formattedTotalCommissions) ? '0.00' : formattedTotalCommissions,
        formattedBonuses,
        formattedTotalCompensation,
      ]);

      // eslint-disable-next-line no-return-assign,no-param-reassign
      summaryRow.eachCell((cell: any) => cell.style = cellStyle);

      worksheet.addRow([]);

      const shiftHeader = worksheet.addRow([
        t('date', { ns: 'timesheets' }),
        t('orders', { ns: 'dashboard' }),
        t('products', { ns: 'dashboard' }),
        t('netSales', { ns: 'commissions' }),
        t('commission', { ns: 'commissions' }),
        t('hoursWorked', { ns: 'timesheets' }),
      ]);

      // eslint-disable-next-line no-return-assign,no-param-reassign
      shiftHeader.eachCell((cell: any) => cell.style = subHeaderStyle);

      const commissionsByDate = getCommissionsByDate(getAttributedOrdersByFilters, user, sheets, shop.shopTimezone);
      const userSheets = sheets.find((s) => s.personnel.email === email);
      const shifts = mapWorkTimesByDate(userSheets?.shifts || [], shop?.shopTimezone);

      commissionsByDate.forEach((detail) => {
        const date = dateFormatTZ(detail.date, 'YYYY-MM-DD', shop.shopTimezone);
        const hoursWorked = get(
          shifts,
          [email, dateFormatTZ(detail.date, 'DD/MM/YYYY', shop.shopTimezone), 'workTimes'],
          0,
        );

        const commission = calculateOrderCommissionByPlan(
          detail.netSales,
          user?.commissionPlan || commissionPlans[0],
          detail.lineItems,
        )?.totalCommission || 0;

        const formattedCommissions = `${getCurrency().mark}${commission}`;
        const formattedNetSale = `${getCurrency().mark}${detail.netSales.toFixed(2)}`;

        const row = worksheet.addRow([
          date,
          detail.ordersCount || 0,
          detail.productsCount || 0,
          formattedNetSale,
          formattedCommissions,
          parseFloat(String(hoursWorked)).toFixed(2),
        ]);

        // eslint-disable-next-line no-return-assign,no-param-reassign
        row.eachCell((cell: any) => cell.style = cellStyle);
      });

      worksheet.addRow([]);
      worksheet.addRow([]);
    }

    worksheet.columns.forEach((col) => {
      let maxLength = 10;
      col.eachCell?.({ includeEmpty: true }, (cell) => {
        const val = cell.value?.toString() || '';
        maxLength = Math.max(maxLength, val.length);
      });
      // eslint-disable-next-line no-param-reassign
      col.width = maxLength + 2;
    });

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/octet-stream' });
    saveAs(blob, `Commissions_${new Date().toISOString().split('T')[0]}.xlsx`);
  }, [getAttributedOrdersByFilters, filteredCommissionsSummary, sheets]);

  const renderTableHeader = useCallback(() => (
    <OrdersTableHeader
      printRef={componentRef}
      handleScrollTable={handleScrollTable}
      apiRef={apiRef}
      setIsFetchByDate={setIsFetchByDate}
      isFullTable={isFullTable}
      setIsFullTable={setIsFullTable}
      handleExport={() => exportCommissionsData()}
      isCommissionView={commissionView === 1}
      isDisplaySelectedMembers

    />
  ), [apiRef, isFullTable, commissionView]);

  const commissionsColumns = useMemo(() => [
    {
      field: 'name',
      headerName: t('name', { ns: 'inputs' }),
      width: 200,
      renderCell: ({ row }) => (
        <Box display="flex" alignItems="center" height="100%">
          <MemoizedBaseAvatar
            statusTooltipText={t('pendingRegistration', { ns: 'team' })}
            status={row.user.status}
            avatarFile={row.user.avatarFile}
            avatarName={row.user.name}
          />
          <Typography variant="caption">
            {startCase(row.user.name)}
          </Typography>
        </Box>
      ),
      valueGetter: (_, row) => get(row, 'user.name', '/'),
    } as GridColDef,
    {
      field: 'ordersCount',
      headerName: t('ordersCount', { ns: 'commissions' }),
      width: 170,
      renderCell: ({ row }) => (
        <Box display="flex" alignItems="center" height="100%">
          <Typography variant="caption">
            {row.ordersCount}
          </Typography>
        </Box>
      ),
      valueGetter: (_, row) => get(row, 'ordersCount', '/'),
    } as GridColDef,
    {
      field: 'productsCount',
      headerName: t('productsCount', { ns: 'commissions' }),
      width: 170,
      renderCell: ({ row }) => (
        <Box display="flex" alignItems="center" height="100%">
          <Typography variant="caption">
            {row.productsCount}
          </Typography>
        </Box>
      ),
    } as GridColDef,
    {
      field: 'totalSales',
      headerName: t('totalSales', { ns: 'commissions' }),
      width: 170,
      renderCell: ({ row }) => (
        <Box display="flex" alignItems="center" height="100%">
          <Typography variant="caption">
            {getCurrency().mark}
            {' '}
            {row.totalSales.toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </Typography>
        </Box>
      ),
    } as GridColDef,
    commissionView === 1 && {
      field: 'commissionPlan',
      headerName: t('commissionPlan', { ns: 'orders' }),
      width: 225,
      renderCell: ({ row }) => {
        const isProductCommission = row.user?.commissionPlan?.type === 'Products';

        // eslint-disable-next-line no-nested-ternary
        const planIcon = isProductCommission
          ? 'fluent-mdl2:product-variant'
          : row.user?.commissionPlan?.type === 'Fixed'
            ? 'mdi:lock-rate-outline'
            : 'lucide-lab:stairs-arrow-up-right';

        // eslint-disable-next-line no-nested-ternary
        const planColor = isProductCommission
          ? '#4a599f'
          : row.user?.commissionPlan?.type === 'Fixed'
            ? '#A855F7'
            : '#005249';

        // eslint-disable-next-line no-nested-ternary
        const labelColor = isProductCommission
          ? 'secondary'
          : row.user?.commissionPlan?.type === 'Fixed'
            ? 'primary'
            : 'success';

        return (
          <Box display="flex" alignItems="center" height="100%" gap={1}>
            <BaseLabel color={labelColor}>
              {startCase(row.user?.commissionPlan?.name) || '/'}
            </BaseLabel>
            {row.user?.commissionPlan?.name && (
            <Iconify
              icon={planIcon}
              width={18}
              sx={{
                color: planColor,
              }}
            />
            )}
          </Box>
        );
      },
    } as GridColDef,
    {
      field: 'totalCommissions',
      headerName: t('totalCommissions', { ns: 'commissions' }),
      width: 200,
      renderCell: ({ row }) => {
        const validCommission = Number.isNaN(row.totalCommissions) ? 0 : row.totalCommissions;

        const commissions = tableCommissionView === 0
          ? parseFloat(
            String(calculateOrderCommissionByPlan(
              row.totalSales,
              row?.user?.commissionPlan || commissionPlans[0],
              row.lineItems,
            )?.totalCommission || 0),
          )
          : parseFloat(row.dailyCommissions || 0);

        return (
          <Box display="flex" alignItems="center" height="100%">
            {commissionView === 0 ? (
              <Typography variant="caption">
                {getCurrency().mark}
                {' '}
                {validCommission?.toFixed(2)}
              </Typography>
            ) : (
              <Typography variant="caption">
                {getCurrency().mark}
                {' '}
                {commissions?.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </Typography>
            )}
            {row.inTeam && (
            <Typography color="primary" pl={1} variant="caption">
              {`(${t('perTeamPlan', { ns: 'orders' })})`}
            </Typography>
            )}
          </Box>
        );
      },
    } as GridColDef,
    {
      field: 'bonuses',
      headerName: t('bonuses', { ns: 'commissions' }),
      width: 200,
      renderCell: ({ row }) => (
        <Box display="flex" flexDirection="column" alignItems="flex-start" height="100%" overflow="auto">
          {row.bonusesApplied?.length > 0 ? (
            row.bonusesApplied?.map((bonus: TBonus & { totalBonus: number }) => (
              <Box gap={0.75} key={bonus.index} display="flex" alignItems="center" height="100%">
                <Typography variant="overline" color="primary" noWrap>
                  {bonus.name}
                </Typography>
                <Typography variant="overline" noWrap>
                  -
                </Typography>
                <Typography variant="overline" noWrap>
                  {getCurrency().mark}
                  {' '}
                  {bonus.totalBonus?.toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </Typography>
              </Box>
            ))
          ) : (
            <Box display="flex" alignItems="center" height="100%">
              <Typography variant="caption">
                {getCurrency().mark}
                {' '}
                0.0
              </Typography>
            </Box>
          )}
        </Box>
      ),
    } as GridColDef,
    {
      field: 'totalCompensation',
      headerName: t('totalCompensation', { ns: 'orders' }),
      width: 170,
      renderCell: ({ row }) => {
        const validCommission = Number.isNaN(row.totalCommissions) ? 0 : parseFloat(row.totalCommissions);

        const commissions = tableCommissionView === 0
          ? parseFloat(
            String(calculateOrderCommissionByPlan(
              row.totalSales,
              row?.user?.commissionPlan || commissionPlans[0],
              row.lineItems,
            )?.totalCommission || 0),
          )
          : parseFloat(row.dailyCommissions || 0);

        const totalBonus = row.bonusesApplied?.reduce((acc: number, bonus: TBonus & { totalBonus: number }) => {
          const bonusValue = parseFloat(String(bonus.totalBonus)) || 0;
          return acc + bonusValue;
        }, 0) || 0;

        const totalCompensation = commissionView === 0
          ? validCommission + totalBonus
          : commissions + totalBonus;

        return (
          <Box display="flex" alignItems="center" height="100%">
            <Typography variant="caption">
              {getCurrency().mark}
              {' '}
              {totalCompensation?.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </Typography>
          </Box>
        );
      },
    } as GridColDef,
  ].filter((x) => !!x) as Array<GridColDef>, [tableCommissionView, commissionView]);

  const renderCustomNoRowsOverlay = () => <CustomNoRowsOverlay text={t('itLooksLikeThereAreNoData')} />;

  const renderDetailPanelContent = useCallback(
    (tableRow: GridValidRowModel) => {
      const commissionsByDate = getCommissionsByDate(getAttributedOrdersByFilters, tableRow.user, sheets, shop.shopTimezone);

      const userSheets = sheets.find((sheet) => sheet.personnel.email === tableRow.user.email);

      const shifts = mapWorkTimesByDate(userSheets?.shifts || [], shop?.shopTimezone);

      return (
        <Box padding={2} bgcolor={isLight ? '#f5f5f5' : ''}>
          <Typography paddingBottom={2} variant="subtitle2">{t('commissionsByDate', { ns: 'commissions' })}</Typography>
          <DataGridPro
            density="compact"
            columns={[
              {
                field: 'date',
                headerName: t('date', { ns: 'timesheets' }),
                width: 170,
                renderCell: ({ row }) => (
                  <Box display="flex" alignItems="center" height="100%">
                    <Typography variant="caption">
                      {dateFormatTZ(row.date, 'DD MMMM, YYYY', shop.shopTimezone)}
                    </Typography>
                  </Box>
                ),
              },
              {
                field: 'ordersCount',
                headerName: t('orders', { ns: 'dashboard' }),
                width: 170,
                renderCell: ({ row }) => (
                  <Box display="flex" alignItems="center" height="100%">
                    <Typography variant="caption">
                      {row.ordersCount || 0}
                    </Typography>
                  </Box>
                ),
              },
              {
                field: 'productsCount',
                headerName: t('products', { ns: 'dashboard' }),
                width: 170,
              },
              {
                field: 'totalSales',
                headerName: t('netSales', { ns: 'commissions' }),
                width: 170,
                renderCell: ({ row }) => (
                  <Box display="flex" alignItems="center" height="100%">
                    <Typography variant="caption">
                      {getCurrency().mark}
                      {' '}
                      {row.netSales.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </Typography>
                  </Box>
                ),
              },
              {
                field: 'amount',
                headerName: t('commission', { ns: 'commissions' }),
                width: 170,
                renderCell: ({ row }) => {
                  const validCommission = Number.isNaN(row.commission) ? 0 : row.commission;

                  return (
                    <Box display="flex" alignItems="center" height="100%">
                      {/* eslint-disable-next-line no-nested-ternary */}
                      {commissionView === 0
                        ? (
                          <Typography variant="caption">
                            {getCurrency().mark}
                            {' '}
                            {validCommission.toLocaleString(undefined, {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}
                          </Typography>
                        ) : tableCommissionView !== 0
                          ? (
                            <Typography variant="caption">
                              {getCurrency().mark}
                              {' '}
                              {calculateOrderCommissionByPlan(
                                row.netSales,
                                tableRow?.user?.commissionPlan || commissionPlans[0],
                                row.lineItems,
                              )?.totalCommission?.toLocaleString(undefined, {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}
                            </Typography>
                          )
                          : <Typography variant="caption">-</Typography>}
                    </Box>
                  );
                },
              },
              {
                headerName: t('hoursWorked', { ns: 'timesheets' }),
                field: 'totals.totalHours',
                minWidth: 170,
                renderCell: ({ row }) => (
                  <Box display="flex" alignItems="center" height="100%">
                    {commissionView === 0 ? (
                      <Typography variant="caption">-</Typography>
                    ) : (
                      <Typography variant="caption">
                        {get(
                          shifts,
                          [tableRow?.user?.email, dateFormatTZ(row.date, 'DD/MM/YYYY', shop.shopTimezone), 'workTimes'],
                          '0.0',
                        ).toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </Typography>
                    )}
                  </Box>
                ),
              },
            ]}
            rows={commissionsByDate as Array<GridValidRowModel>}
            getRowId={(row) => row.index}
            sx={{
              flex: 1,
              '& .MuiDataGrid-columnHeaders': {
                backgroundColor: '#f5f5f5 !important',
              },
              '& .MuiDataGrid-columnHeaders div[role="row"]': {
                backgroundColor: '#f5f5f5 !important',
              },
            }}
            hideFooter
          />
        </Box>
      );
    },
    [getAttributedOrdersByFilters, commissionView, tableCommissionView, sheets],
  );

  const tabs = displayCommissionOptions.map((option, index) => ({
    value: option.value,
    label: option.name,
    color: INDEX_TO_COLOR[index],
  }));

  return (
    <PrintWrapper componentRef={componentRef}>
      <div ref={resizableDiv}>
        <Card
          sx={{
            width: 'auto',
            px: 0,
            padding: 0,
            pb: 8,
            // eslint-disable-next-line no-nested-ternary
            height: exporting ? 'auto' : (isFullTable && filteredCommissionsSummary?.length > 0 ? 'auto' : pageHeight !== 0 && !isPrintMode && !isDesktopSmallScreen ? pageHeight : 'auto'),
            ...(isMobile && {
              background: 'unset',
              boxShadow: 'unset',
              px: 0,
              pt: 0,
              pb: '35px',
              ...(commissionsSummary.length > 0) && {
                height: 'auto',
              },
            }),
          }}
        >
          <MemoizedFilterTabs tabs={tabs} onFilterChange={handleFilterStatus} initialFilter={commissionView} />
          <DataGridPro
            apiRef={apiRef}
            rows={filteredCommissionsSummary}
            slots={{
              toolbar: renderTableHeader,
              noRowsOverlay: renderCustomNoRowsOverlay,
              footer: OrdersCountLoader as any,
            }}
            sx={{
              '.MuiDataGrid-main > *:first-of-type': {
                borderTopLeftRadius: 0,
                borderTopRightRadius: 0,
              },
              px: 0,
            }}
            localeText={localText}
            slotProps={{
              toolbar: {
                showQuickFilter: true,
                printOptions: {
                  hideFooter: true,
                  hideToolbar: true,
                },
              },
            }}
            showToolbar
            getRowId={(row) => row.user?.email || row.user?.orderId}
            onRowSelectionModelChange={(emails) => {
              dispatch(sheetsActions.setSelectedMembers(emails));
            }}
            columns={commissionsColumns}
            hideFooterPagination
            hideFooterSelectedRowCount
            disableRowSelectionOnClick
            checkboxSelection={isOpenEmployeesExportMode}
            className={isPrintMode ? 'print-mode' : ''}
            getDetailPanelContent={({ row }) => renderDetailPanelContent(row)}
            getDetailPanelHeight={() => 'auto'}
          />
        </Card>
      </div>
    </PrintWrapper>
  );
};

export {
  CommissionsTable,
};
